import React from 'react';

import { Stack } from '@atlaskit/primitives/compiled';

import { ProjectHighlightsElement } from '../sub-agents/components/project-highlights/project-highlights';
import { RelatedPeopleElement } from '../sub-agents/components/related-people/related-people';
import type { ActionElementType, SmartLinkElementType, SmartLinkListElementType } from '../types';

import { CarouselElement } from './carousel-element';
import { MarkdownElement } from './markdown-element';
import { SmartLinkElement } from './smartlink-element';
import type { ElementRenderer, ElementTag } from './types';

/**
 * Adapter for smart link elements
 * Ensures all smart links are rendered as standalone cards
 */
const SmartLinkElementAdapter: ElementRenderer = ({
	component,
	variant,
	isStreaming,
	sectionKey,
	containerType,
	indexInContainer,
}) => (
	<SmartLinkElement
		component={component as SmartLinkElementType}
		variant={variant}
		isStreaming={isStreaming}
		sectionKey={sectionKey}
		containerType={containerType}
		indexInContainer={indexInContainer}
	/>
);

/**
 * Adapter for action card elements
 * Converts action card elements into smart link elements with validation
 */
const ActionCardElementAdapter: ElementRenderer = ({
	component,
	variant,
	isStreaming,
	sectionKey,
	containerType,
	indexInContainer,
}) => {
	const actionCard = component as ActionElementType;

	// Validate component has a url and convert to smart link
	if (!actionCard.attributes?.url) {
		return null;
	}

	const smartLinkComponent: SmartLinkElementType = {
		tag: 'smart-link',
		attributes: {
			url: actionCard.attributes.url,
			title: actionCard.attributes?.title,
			type: 'action',
			pos: actionCard.attributes?.pos,
		},
	};

	return (
		<SmartLinkElement
			component={smartLinkComponent}
			variant={variant}
			isStreaming={isStreaming}
			sectionKey={sectionKey}
			containerType={containerType}
			indexInContainer={indexInContainer}
		/>
	);
};

/**
 * Adapter for related people sub-agent elements
 * Extracts related people data from the sub-agent data map
 */
const RelatedPeopleElementAdapter: ElementRenderer = ({ subAgentDataMap, isStreaming }) => (
	<RelatedPeopleElement data={subAgentDataMap.relatedPeople} isStreaming={isStreaming} />
);

/**
 * Adapter for project highlights sub-agent elements
 * Extracts project highlights data from the sub-agent data map
 */
const ProjectHighlightsElementAdapter: ElementRenderer = ({ subAgentDataMap, isStreaming }) => (
	<ProjectHighlightsElement data={subAgentDataMap.projectHighlights} isStreaming={isStreaming} />
);


/**
 * Adapter for smart link list elements
 * Renders a vertical stack of smart link cards from item children
 */
const SmartLinkListElementAdapter: ElementRenderer = ({
	component,
	variant,
	isStreaming,
	sectionKey,
	containerType,
	indexInContainer,
}) => {
	const listComponent = component as SmartLinkListElementType;
	const items = Array.isArray(listComponent.children) ? listComponent.children : [];

	return (
		<Stack space="space.100" testId="lumina-layout-smart-link-list">
			{items.map((item, _index) => {
				if (typeof item === 'string') {
					return null;
				}
				const attrs = item.attributes as Record<string, string | undefined> | undefined;
				const url = attrs?.['url'];
				if (!url) {
					return null;
				}
				const smartLinkComponent: SmartLinkElementType = {
					tag: 'smart-link',
					attributes: {
						url,
						title: attrs?.['title'],
						pos: attrs?.['pos'],
					},
				};
				return (
					<SmartLinkElement
						key={url}
						component={smartLinkComponent}
						variant={variant}
						isStreaming={isStreaming}
						sectionKey={sectionKey}
						containerType={containerType}
						indexInContainer={indexInContainer}
					/>
				);
			})}
		</Stack>
	);
};

/**
 * Centralized map of element renderers
 * Maps element tags to their corresponding component references
 *
 * Components with direct references (MarkdownElement, CarouselElement) require no adaptation.
 * Components with adapters handle prop mapping/data transformation.
 */
export const elementRenderers: Record<ElementTag, ElementRenderer> = {
	'action-card': ActionCardElementAdapter,
	carousel: CarouselElement as ElementRenderer,
	md: MarkdownElement as ElementRenderer,
	'project-highlights': ProjectHighlightsElementAdapter,
	'related-people': RelatedPeopleElementAdapter,
	'smart-link': SmartLinkElementAdapter,
	'smart-link-list': SmartLinkListElementAdapter,
};
